/**
 * ESC/POS Thermal Printer Utility
 * Direct printing to thermal printer via Bluetooth
 */

class ESCPOSPrinter {
  constructor() {
    this.device = null;
    this.characteristic = null;
    this.encoder = new TextEncoder();
    this.isConnected = false;
    this.reconnectAttempts = 0;
    this.maxReconnectAttempts = 3;
    
    // Random pantun collection
    this.pantunRental = [
      {
        baris1: 'Beli makan ke warung Mbok Ijah',
        baris2: 'Jangan lupa beli kerupuk',
        baris3: 'Main game sampai puas sudah',
        baris4: 'Jangan lupa balik lagi yuk!'
      },
      {
        baris1: 'Pergi ke pasar beli durian',
        baris2: 'Pulangnya singgah beli sate',
        baris3: 'Main PS di Rimuru menyenangkan',
        baris4: 'Kapan-kapan main lagi ya te!'
      },
      {
        baris1: 'Ke toko beli baju batik',
        baris2: 'Sekalian beli topi bagus',
        baris3: 'Gaming seru sampai jam plastik',
        baris4: 'Makasih ya udah main di tempat kami pus!'
      },
      {
        baris1: 'Naik sepeda keliling kota',
        baris2: 'Mampir beli es campur segar',
        baris3: 'PS5 grafiknya mantap sekali kita',
        baris4: 'Balik lagi ya jangan sampai lupa!'
      },
      {
        baris1: 'Makan bakso di pinggir jalan',
        baris2: 'Minumnya teh manis hangat',
        baris3: 'Main game bikin lupa dengan kejenuhan',
        baris4: 'Sampai jumpa lagi di lain waktu semangat!'
      }
    ];
    
    this.pantunFnB = [
      {
        baris1: 'Makan seblak pakai keju',
        baris2: 'Sambil ngopi ditemani senja',
        baris3: 'Order F&B di Rimuru Game asyik terus',
        baris4: 'Balik lagi ya jangan lupa!'
      },
      {
        baris1: 'Beli gorengan di warung Pak Bambang',
        baris2: 'Jangan lupa minum es teh manis',
        baris3: 'Ngemil sambil main PS memang kurang ajar',
        baris4: 'Terima kasih banyak atas kunjungannya kakak manis!'
      },
      {
        baris1: 'Ke kantin beli martabak telur',
        baris2: 'Minumnya susu coklat dingin',
        baris3: 'Cemilan enak bikin kenyang perut',
        baris4: 'Datang lagi ya ke sini jangan malu-malu inget!'
      },
      {
        baris1: 'Jalan-jalan ke mall beli sepatu',
        baris2: 'Pulangnya bawa oleh-oleh kue',
        baris3: 'F&B di sini enak-enak semua',
        baris4: 'Lain kali pesan lagi ya bro!'
      },
      {
        baris1: 'Beli siomay di abang-abang',
        baris2: 'Sambil nungguin hujan reda',
        baris3: 'Makanan ringan jadi teman main game yang senang',
        baris4: 'Sampai ketemu lagi di lain kesempatan ya!'
      }
    ];
  }
  
  /**
   * Get random pantun
   */
  getRandomPantun(type = 'rental') {
    const collection = type === 'fnb' ? this.pantunFnB : this.pantunRental;
    const randomIndex = Math.floor(Math.random() * collection.length);
    return collection[randomIndex];
  }

  // ESC/POS Commands
  ESC = '\x1B';
  GS = '\x1D';
  
  // Text alignment
  ALIGN_LEFT = this.ESC + 'a' + '\x00';
  ALIGN_CENTER = this.ESC + 'a' + '\x01';
  ALIGN_RIGHT = this.ESC + 'a' + '\x02';
  
  // Text style
  BOLD_ON = this.ESC + 'E' + '\x01';
  BOLD_OFF = this.ESC + 'E' + '\x00';
  
  UNDERLINE_ON = this.ESC + '-' + '\x01';
  UNDERLINE_OFF = this.ESC + '-' + '\x00';
  
  // Text size (width x height)
  NORMAL_SIZE = this.GS + '!' + '\x00';
  DOUBLE_HEIGHT = this.GS + '!' + '\x01';
  DOUBLE_WIDTH = this.GS + '!' + '\x10';
  DOUBLE_SIZE = this.GS + '!' + '\x11';
  
  // Feed & Cut
  LINE_FEED = '\x0A';
  CUT_PAPER = this.GS + 'V' + '\x41' + '\x00';
  
  // Initialize printer
  INIT = this.ESC + '@';

  /**
   * Check if already connected and valid
   */
  isDeviceConnected() {
    return this.device && 
           this.device.gatt && 
           this.device.gatt.connected && 
           this.characteristic;
  }

  /**
   * Request Bluetooth connection to printer
   * @param {boolean} forceNew - Force new device picker
   */
  async connect(forceNew = false) {
    try {
      console.log('🔍 [Connect] Starting connection attempt...');
      console.log('🔍 [Connect] forceNew:', forceNew);
      console.log('🔍 [Connect] Current device:', this.device ? 'exists' : 'null');
      console.log('🔍 [Connect] Current characteristic:', this.characteristic ? 'exists' : 'null');
      console.log('🔍 [Connect] isDeviceConnected():', this.isDeviceConnected());
      
      // If already connected and not forcing new, reuse connection
      if (!forceNew && this.isDeviceConnected()) {
        console.log('✅ [Connect] Already connected, reusing connection');
        this.isConnected = true;
        return true;
      }
      
      // If device exists but disconnected, try to reconnect
      if (!forceNew && this.device && this.device.gatt && !this.device.gatt.connected) {
        console.log('🔄 [Connect] Attempting to reconnect to previous device...');
        try {
          const server = await this.device.gatt.connect();
          console.log('🔄 [Connect] GATT connected, getting service...');
          
          const service = await server.getPrimaryService('000018f0-0000-1000-8000-00805f9b34fb');
          console.log('🔄 [Connect] Service obtained, getting characteristic...');
          
          this.characteristic = await service.getCharacteristic('00002af1-0000-1000-8000-00805f9b34fb');
          console.log('🔄 [Connect] Characteristic obtained');
          
          // Wait a bit to ensure device is fully ready
          await new Promise(resolve => setTimeout(resolve, 300));
          
          this.isConnected = true;
          console.log('✅ [Connect] Reconnected to previous printer!');
          return true;
        } catch (e) {
          console.log('❌ [Connect] Reconnect failed:', e.message);
          // Clear invalid device/characteristic
          this.device = null;
          this.characteristic = null;
          this.isConnected = false;
        }
      }
      
      // Show device picker for new connection
      console.log('📱 [Connect] Requesting Bluetooth Device...');
      
      this.device = await navigator.bluetooth.requestDevice({
        filters: [
          { services: ['000018f0-0000-1000-8000-00805f9b34fb'] }, // Serial Port
        ],
        optionalServices: ['000018f0-0000-1000-8000-00805f9b34fb']
      });
      
      console.log('📱 [Connect] Device selected:', this.device.name);

      // Setup disconnect listener
      this.device.addEventListener('gattserverdisconnected', () => {
        console.log('🔌 [Disconnect] Printer disconnected');
        this.isConnected = false;
        this.characteristic = null;
      });

      console.log('🔗 [Connect] Connecting to GATT Server...');
      const server = await this.device.gatt.connect();
      console.log('✅ [Connect] GATT Server connected');
      
      console.log('🔍 [Connect] Getting Service...');
      const service = await server.getPrimaryService('000018f0-0000-1000-8000-00805f9b34fb');
      console.log('✅ [Connect] Service obtained');
      
      console.log('🔍 [Connect] Getting Characteristic...');
      this.characteristic = await service.getCharacteristic('00002af1-0000-1000-8000-00805f9b34fb');
      console.log('✅ [Connect] Characteristic obtained');
      
      // CRITICAL: Wait for device to be fully ready before marking as connected
      // This prevents race condition where print command is sent before device is ready
      console.log('⏳ [Connect] Waiting for device to stabilize...');
      await new Promise(resolve => setTimeout(resolve, 300));
      
      // Verify characteristic is still valid
      if (!this.characteristic) {
        throw new Error('Characteristic lost after connection');
      }
      
      // Save device name for display
      if (this.device && this.device.name) {
        localStorage.setItem('thermal_printer_name', this.device.name);
        console.log('💾 [Connect] Printer saved:', this.device.name);
      }
      
      this.isConnected = true;
      console.log('✅ [Connect] Successfully connected to printer!');
      console.log('✅ [Connect] Device ready for printing');
      return true;
    } catch (error) {
      console.error('❌ [Connect] Bluetooth connection failed:', error);
      console.error('❌ [Connect] Error details:', error.message, error.stack);
      
      // Clean up on failure
      this.device = null;
      this.characteristic = null;
      this.isConnected = false;
      
      throw new Error('Gagal connect ke printer: ' + error.message);
    }
  }
  
  /**
   * Get saved printer name
   */
  getSavedPrinterName() {
    return localStorage.getItem('thermal_printer_name') || 'No printer saved';
  }
  
  /**
   * Forget saved printer and disconnect
   */
  forgetPrinter() {
    this.disconnect();
    localStorage.removeItem('thermal_printer_name');
    this.device = null;
    this.characteristic = null;
    this.isConnected = false;
    console.log('Saved printer forgotten');
  }

  /**
   * Send data to printer
   */
  async write(data) {
    console.log('📝 [Write] Attempting to write data...');
    console.log('📝 [Write] Characteristic exists:', !!this.characteristic);
    console.log('📝 [Write] Device connected:', this.isDeviceConnected());
    
    if (!this.characteristic) {
      console.error('❌ [Write] No characteristic available');
      throw new Error('Printer not connected');
    }

    try {
      // Convert string to bytes
      const bytes = typeof data === 'string' 
        ? this.encoder.encode(data)
        : data;
      
      console.log(`📝 [Write] Data size: ${bytes.length} bytes`);
      
      // Split into chunks (max 512 bytes per write for some printers)
      const chunkSize = 512;
      const totalChunks = Math.ceil(bytes.length / chunkSize);
      console.log(`📝 [Write] Sending ${totalChunks} chunk(s)...`);
      
      for (let i = 0; i < bytes.length; i += chunkSize) {
        const chunk = bytes.slice(i, i + chunkSize);
        const chunkNum = Math.floor(i / chunkSize) + 1;
        console.log(`📝 [Write] Sending chunk ${chunkNum}/${totalChunks} (${chunk.length} bytes)`);
        
        await this.characteristic.writeValue(chunk);
        
        // Small delay between chunks
        await new Promise(resolve => setTimeout(resolve, 50));
      }
      
      console.log('✅ [Write] Data sent successfully');
    } catch (error) {
      console.error('❌ [Write] Write error:', error);
      console.error('❌ [Write] Error details:', error.message);
      throw error;
    }
  }

  /**
   * Print text with formatting
   */
  async printText(text, options = {}) {
    let output = '';
    
    // Alignment
    if (options.align === 'center') output += this.ALIGN_CENTER;
    else if (options.align === 'right') output += this.ALIGN_RIGHT;
    else output += this.ALIGN_LEFT;
    
    // Text size
    if (options.size === 'double') output += this.DOUBLE_SIZE;
    else if (options.size === 'wide') output += this.DOUBLE_WIDTH;
    else if (options.size === 'tall') output += this.DOUBLE_HEIGHT;
    else output += this.NORMAL_SIZE;
    
    // Bold
    if (options.bold) output += this.BOLD_ON;
    
    // Underline
    if (options.underline) output += this.UNDERLINE_ON;
    
    // Text
    output += text;
    
    // Reset styles
    if (options.bold) output += this.BOLD_OFF;
    if (options.underline) output += this.UNDERLINE_OFF;
    
    // Line feed
    output += this.LINE_FEED;
    
    await this.write(output);
  }

  /**
   * Print line separator
   */
  async printLine(char = '-', length = 32) {
    await this.printText(char.repeat(length), { align: 'center' });
  }

  /**
   * Print receipt for F&B order
   */
  async printFnBReceipt(order) {
    try {
      // Initialize
      await this.write(this.INIT);
      
      // Get business profile and branch info
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      const businessProfile = await this.getBusinessProfile();
      const branchInfo = await this.getBranchInfo(user.branch_id);
      
      // Header - Business Name (normal size agar muat 1 baris)
      if (businessProfile && businessProfile.business_name) {
        await this.printText(businessProfile.business_name.toUpperCase(), { align: 'center', bold: true });
      }
      
      // Branch Name - PRIORITAS: user.branch (fallback ke branchInfo.name)
      const branchName = user.branch || (branchInfo && branchInfo.name) || '';
      if (branchName) {
        await this.printText(branchName, { align: 'center', bold: true });
      }
      
      // Branch Address (optional)
      if (branchInfo && branchInfo.address) {
        await this.printText(branchInfo.address, { align: 'center' });
      }
      
      // Branch Phone (optional)
      if (branchInfo && branchInfo.phone) {
        await this.printText(`Telp: ${branchInfo.phone}`, { align: 'center' });
      }
      
      await this.printLine('-');
      await this.printText('Powered by Rimuru Game Suite', { align: 'center' });
      await this.printLine('=');
      
      await this.printText('Order F&B', { align: 'center', bold: true });
      await this.printLine('-');
      
      // Kasir info
      const kasirName = user.full_name || user.username || 'Unknown';
      await this.printText(`Kasir: ${kasirName}`, {});
      await this.printLine('-');
      
      // Customer & Date
      await this.printText(`Customer: ${order.customer_name}`, { bold: true });
      await this.printText(`Tanggal: ${new Date(order.created_at || Date.now()).toLocaleString('id-ID')}`, {});
      await this.printLine();
      
      // Items
      for (const item of order.items) {
        const qty = `${item.quantity}x`;
        const name = item.name;
        const price = `Rp${(item.price * item.quantity).toLocaleString('id-ID')}`;
        
        // Format: "2x Aqua             Rp 4,000"
        const line = `${qty} ${name}`.padEnd(20, ' ') + price.padStart(12, ' ');
        await this.printText(line);
      }
      
      await this.printLine();
      
      // Total
      await this.printText('TOTAL', { bold: true });
      await this.printText(`Rp ${order.total.toLocaleString('id-ID')}`, { 
        align: 'right', 
        bold: true, 
        size: 'wide' 
      });
      
      // Payment info
      if (order.payment_method) {
        await this.printLine();
        await this.printText(`Pembayaran: ${order.payment_method.toUpperCase()}`);
        if (order.amount_received) {
          await this.printText(`Diterima: Rp ${order.amount_received.toLocaleString('id-ID')}`);
        }
        if (order.change) {
          await this.printText(`Kembalian: Rp ${order.change.toLocaleString('id-ID')}`);
        }
      }
      
      await this.printLine('=');
      
      // Footer with Random Pantun
      await this.printText('Terima kasih!', { align: 'center', bold: true });
      await this.write(this.LINE_FEED);
      
      const pantun = this.getRandomPantun('fnb');
      await this.printText(pantun.baris1, { align: 'center' });
      await this.printText(pantun.baris2, { align: 'center' });
      await this.printText(pantun.baris3, { align: 'center' });
      await this.printText(pantun.baris4, { align: 'center', bold: true });
      
      // Feed lines before cut
      await this.write(this.LINE_FEED + this.LINE_FEED + this.LINE_FEED);
      
      // Cut paper
      await this.write(this.CUT_PAPER);
      
      console.log('✅ Print complete!');
      return true;
    } catch (error) {
      console.error('Print error:', error);
      throw error;
    }
  }

  /**
   * Print receipt for Rental
   */
  async printRentalReceipt(rental) {
    try {
      // Initialize
      await this.write(this.INIT);
      
      // Get business profile and branch info
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      const businessProfile = await this.getBusinessProfile();
      const branchInfo = await this.getBranchInfo(rental.branch_id || user.branch_id);
      
      // Header - Business Name (normal size agar muat 1 baris)
      if (businessProfile && businessProfile.business_name) {
        await this.printText(businessProfile.business_name.toUpperCase(), { align: 'center', bold: true });
      }
      
      // Branch Name - PRIORITAS: rental.branch (fallback ke branchInfo.name)
      const branchName = rental.branch || (branchInfo && branchInfo.name) || '';
      if (branchName) {
        await this.printText(branchName, { align: 'center', bold: true });
      }
      
      // Branch Address (optional)
      if (branchInfo && branchInfo.address) {
        await this.printText(branchInfo.address, { align: 'center' });
      }
      
      // Branch Phone (optional)
      if (branchInfo && branchInfo.phone) {
        await this.printText(`Telp: ${branchInfo.phone}`, { align: 'center' });
      }
      
      await this.printLine('-');
      await this.printText('Powered by Rimuru Game Suite', { align: 'center' });
      await this.printLine('=');
      
      // Kasir info
      const kasirName = user.full_name || user.username || 'Unknown';
      await this.printText(`Kasir: ${kasirName}`, {});
      await this.printLine('-');
      
      // Customer Info
      await this.printText(`Customer: ${rental.customer_name}`, { bold: true });
      await this.printText(`Unit: ${rental.ps_unit_name}`);
      await this.printText(`Mode: ${rental.rental_mode.toUpperCase()}`);
      
      // Duration
      const duration = rental.actual_duration_hours || rental.duration_hours || 0;
      await this.printText(`Durasi: ${duration.toFixed(1)} jam`);
      
      await this.printLine();
      
      // Calculate prices correctly
      const totalAmount = rental.final_price || rental.total_price || 0;
      const fnbTotal = (rental.additional_items || []).reduce((sum, item) => 
        sum + (item.price * (item.qty || item.quantity || 1)), 0);
      const baseRentalPrice = totalAmount - fnbTotal;
      
      await this.printText(`Harga Rental:`.padEnd(20, ' ') + `Rp${baseRentalPrice.toLocaleString('id-ID')}`.padStart(12, ' '));
      
      // F&B Items
      if (rental.additional_items && rental.additional_items.length > 0) {
        await this.printLine();
        await this.printText('F&B Items:', { bold: true });
        
        for (const item of rental.additional_items) {
          const qty = item.qty || item.quantity || 1;
          const name = item.name;
          const price = item.price * qty;
          
          const line = `${qty}x ${name}`.padEnd(20, ' ') + `Rp${price.toLocaleString('id-ID')}`.padStart(12, ' ');
          await this.printText(line);
        }
      }
      
      await this.printLine('=');
      
      // Total
      await this.printText('TOTAL:', { bold: true });
      await this.printText(`Rp ${totalAmount.toLocaleString('id-ID')}`, { 
        align: 'right', 
        bold: true, 
        size: 'wide' 
      });
      
      // Payment info
      if (rental.payment_method) {
        await this.printLine();
        await this.printText(`Metode: ${rental.payment_method.toUpperCase()}`);
        if (rental.amount_received) {
          await this.printText(`Diterima: Rp ${rental.amount_received.toLocaleString('id-ID')}`);
        }
        if (rental.change) {
          await this.printText(`Kembalian: Rp ${rental.change.toLocaleString('id-ID')}`);
        }
      }
      
      await this.printLine('=');
      
      // Date
      const date = new Date(rental.paid_at || rental.actual_end_time || Date.now());
      await this.printText(date.toLocaleString('id-ID'), { align: 'center' });
      
      await this.printLine();
      
      // Footer with Random Pantun
      await this.printText('Terima kasih!', { align: 'center', bold: true });
      await this.write(this.LINE_FEED);
      
      const pantun = this.getRandomPantun('rental');
      await this.printText(pantun.baris1, { align: 'center' });
      await this.printText(pantun.baris2, { align: 'center' });
      await this.printText(pantun.baris3, { align: 'center' });
      await this.printText(pantun.baris4, { align: 'center', bold: true });
      
      // Feed lines before cut
      await this.write(this.LINE_FEED + this.LINE_FEED + this.LINE_FEED);
      
      // Cut paper
      await this.write(this.CUT_PAPER);
      
      console.log('✅ Print complete!');
      return true;
    } catch (error) {
      console.error('Print error:', error);
      throw error;
    }
  }

  /**
   * Get business profile from API
   */
  async getBusinessProfile() {
    try {
      const token = localStorage.getItem('token');
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      
      const response = await fetch(`${API_URL}/api/business-profile`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        return data.profile;
      }
    } catch (error) {
      console.error('Error fetching business profile:', error);
    }
    return null;
  }

  /**
   * Get branch info from API
   */
  async getBranchInfo(branchId) {
    try {
      if (!branchId) return null;
      
      const token = localStorage.getItem('token');
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      
      const response = await fetch(`${API_URL}/api/branches/${branchId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        return data.branch;
      }
    } catch (error) {
      console.error('Error fetching branch info:', error);
    }
    return null;
  }

  /**
   * Disconnect from printer (optional, usually keep connected)
   */
  disconnect() {
    if (this.device && this.device.gatt && this.device.gatt.connected) {
      this.device.gatt.disconnect();
      this.isConnected = false;
      console.log('Disconnected from printer');
    }
  }
}

// Export singleton instance
export const escposPrinter = new ESCPOSPrinter();

// Export class for multiple instances if needed
export default ESCPOSPrinter;
